<script lang="ts" setup>
import { computed, onMounted } from 'vue'
import { keyBy } from 'lodash'
import { useI18n } from 'vue-i18n'

import { uuid } from '@/types/general'
import { useProductStore } from '@/stores/product-store'
import { CompanyProduct } from '@/types/packaging'
import { useAuthStore } from '@/stores/auth-store'
import { LicenseType } from '@/types/company'

import ReorderableProducts from '@/components/packaging/products/ReorderableProducts.vue'
import ProductItem from '@/components/packaging/products/ProductItem.vue'

const products = defineModel<{ productId: uuid; order: number }[]>()

const { t } = useI18n()
const productStore = useProductStore()
const authStore = useAuthStore()

const companyProducts = computed(() => keyBy(productStore.companyProducts, 'id'))

const defaultProducts = computed<CompanyProduct[]>({
    get: () => {
        if (!products.value) return []

        return products.value
            .map((roleProduct, index) => {
                const product = companyProducts.value[roleProduct.productId]
                return product ? { ...product, order: roleProduct.order ?? index + 1 } : undefined
            })
            .filter((product): product is CompanyProduct => !!product)
    },
    set: (newProductOrder: CompanyProduct[]) => {
        products.value = newProductOrder.map((product, index) => ({
            productId: product.id,
            order: index + 1,
        }))
    },
})

const selectableProducts = computed(() => {
    return productStore.companyProducts.filter((companyProduct) => {
        return !products.value!.find((product) => product.productId === companyProduct.id)
    })
})

async function fetchProducts() {
    if (!authStore.hasLicense(LicenseType.PackagingModule)) return
    if (productStore.companyProducts.length === 0) {
        await productStore.fetchCompanyProducts()
    }
    if (products.value!.length === 0) {
        products.value = productStore.defaultProducts.map((defaultProduct, index) => ({
            productId: defaultProduct.id,
            order: index + 1,
        }))
    }
}

function toggleFavoriteProduct(product: CompanyProduct) {
    // If the product is already a favorite, remove it
    if (products.value!.find((existingProduct) => existingProduct.productId === product.id)) {
        products.value = products
            .value!.filter((existingProduct) => existingProduct.productId !== product.id)
            .map((existingProduct, index) => ({
                ...existingProduct,
                order: index + 1,
            }))
    } else {
        // Add it to the favorites at the end of the list
        products.value = [
            ...products.value!,
            { productId: product.id, order: products.value!.length + 1 },
        ]
    }
}

onMounted(fetchProducts)
</script>

<template>
    <div class="flex flex-col">
        <div v-if="defaultProducts.length > 0">
            <h3
                class="relative text-lg font-bold uppercase text-primary-400"
                v-text="t('customFavoriteProducts')"
            />
            <ReorderableProducts
                v-model="defaultProducts"
                class="mb-14 mt-5 grid gap-6 grid-cols-3"
                simple-product-card
                @favorite-clicked="toggleFavoriteProduct($event)"
            />
        </div>

        <div v-if="selectableProducts.length > 0">
            <div class="flex items-center space-x-1">
                <h3
                    class="text-lg font-bold uppercase text-primary-400"
                    v-text="t('hiddenProducts')"
                />
                <mdi:information
                    v-tooltip="t('hiddenProductsExplanation')"
                    class="text-primary-400 h-4 w-4"
                />
            </div>
            <div class="mb-14 mt-5 grid gap-6 grid-cols-3">
                <ProductItem
                    v-for="product in selectableProducts"
                    :key="product.id"
                    :product="product"
                    class="opacity-50"
                    show-favorite-icon
                    simple-product-card
                    @favorite-clicked="toggleFavoriteProduct(product)"
                />
            </div>
        </div>
    </div>
</template>
